/**
 * <s-fit-text> — keeps its first child heading within data-max-lines lines.
 *
 * Starts from the CSS font size and steps it down by 10% (or by data-step pixels) until the text fits, never going
 * below data-min-desktop (>=768px) / data-min-mobile pixels. Re-runs on resize and once the
 * web fonts have loaded (font-display: swap changes the metrics).
 */
class SFitText extends HTMLElement {
  #observer;
  #frame = 0;
  #width = 0;

  connectedCallback() {
    this.#fit();
    document.fonts?.ready.then(() => this.isConnected && this.#fit());
    if ('ResizeObserver' in window) {
      this.#observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width || 0;
        if (Math.abs(width - this.#width) < 1) return;
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#fit());
      });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #fit() {
    const heading = this.firstElementChild;
    if (!(heading instanceof HTMLElement)) return;
    this.#width = this.getBoundingClientRect().width;
    heading.style.removeProperty('font-size');

    const maxLines = Number(this.dataset.maxLines) || 3;
    const desktop = window.matchMedia('(min-width: 768px)').matches;
    const min = Number(desktop ? this.dataset.minDesktop : this.dataset.minMobile) || 0;
    const stepPx = Number(this.dataset.step) || 0;
    let size = parseFloat(getComputedStyle(heading).fontSize);

    for (let step = 0; step < 12 && this.#lines(heading) > maxLines && size > min; step += 1) {
      size = Math.max(min, stepPx > 0 ? size - stepPx : Math.round(size * 0.9 * 10) / 10);
      heading.style.fontSize = `${size}px`;
    }
  }

  #lines(heading) {
    const style = getComputedStyle(heading);
    const fontSize = parseFloat(style.fontSize);
    let lineHeight = parseFloat(style.lineHeight);
    if (!Number.isFinite(lineHeight)) lineHeight = fontSize * 0.92;
    return Math.round(heading.getBoundingClientRect().height / lineHeight);
  }
}

if (!customElements.get('s-fit-text')) {
  customElements.define('s-fit-text', SFitText);
}
